/**
 * Use customer route view model.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { useCustomerAuth } from '../customerAuthContext'
import type { CustomerRouteMode } from '../routes'
import { useCustomerRouteGuards } from './customerRouteGuards'
import { useCustomerRouteData } from './useCustomerRouteData'

type UseCustomerRouteViewModelInput = {
  mode: CustomerRouteMode
  currentSlug: string
  onNavigate: (targetPath: string) => void
}

/**
 * Use customer route view model.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const useCustomerRouteViewModel = ({
  mode,
  currentSlug,
  onNavigate,
}: UseCustomerRouteViewModelInput) => {
  const {
    token,
    authenticated,
    initializing,
    busy,
    account,
    profile,
    error,
    clearError,
    login,
    verifyLoginSecondFactor,
    verifyLoginSecondFactorWithPasskey,
    signup,
    updateProfile,
    logout,
  } = useCustomerAuth()
  const {
    nextPath,
    nextPathReady,
    nestedPath,
    isDashboardRoute,
    isOrdersListRoute,
    isOrderDetailRoute,
    isSupportRoute,
    isSupportDetailRoute,
    isPrivacyRoute,
    isProfileRoute,
    isAddressesRoute,
    isPreferencesRoute,
    orderDetailId,
    supportDetailId,
    requestedAccountPath,
    checkoutResult,
    checkoutSessionId,
    orders,
    ordersLoading,
    ordersError,
    retryOrders,
    orderDetail,
    orderDetailLoading,
    orderDetailError,
    orderInvoices,
    orderInvoicesLoading,
    orderInvoicesError,
    checkoutSyncActive,
    supportRequests,
    supportLoading,
    supportError,
    supportSubmitting,
    supportSuccess,
    supportSubject,
    supportMessage,
    supportOrderId,
    supportCategory,
    supportPriority,
    setSupportSubject,
    setSupportMessage,
    setSupportOrderId,
    setSupportCategory,
    setSupportPriority,
    submitSupportRequest,
    retrySupportRequests,
    privacyDashboard,
    privacyLoading,
    privacyError,
    privacyRefreshing,
    exportRequesting,
    exportDownloading,
    consentsSaving,
    deletionSubmitting,
    privacyNotice,
    refreshPrivacyDashboard,
    requestPrivacyExport,
    downloadPrivacyExport,
    updatePrivacyConsents,
    requestAccountDeletion,
  } = useCustomerRouteData({
    currentSlug,
    authenticated,
    token,
  })

  useCustomerRouteGuards({
    initializing,
    accountRoles: account?.roles,
    authenticated,
    mode,
    nextPath,
    nextPathReady,
    onNavigate,
  })

  const handleLogout = () => {
    void logout()
  }

  return {
    token,
    authenticated,
    initializing,
    busy,
    account,
    profile,
    error,
    clearError,
    login,
    verifyLoginSecondFactor,
    verifyLoginSecondFactorWithPasskey,
    signup,
    updateProfile,
    nextPath,
    nextPathReady,
    nestedPath,
    isDashboardRoute,
    isOrdersListRoute,
    isOrderDetailRoute,
    isSupportRoute,
    isSupportDetailRoute,
    isPrivacyRoute,
    isProfileRoute,
    isAddressesRoute,
    isPreferencesRoute,
    orderDetailId,
    supportDetailId,
    requestedAccountPath,
    checkoutResult,
    checkoutSessionId,
    orders,
    ordersLoading,
    ordersError,
    retryOrders,
    orderDetail,
    orderDetailLoading,
    orderDetailError,
    orderInvoices,
    orderInvoicesLoading,
    orderInvoicesError,
    checkoutSyncActive,
    supportRequests,
    supportLoading,
    supportError,
    supportSubmitting,
    supportSuccess,
    supportSubject,
    supportMessage,
    supportOrderId,
    supportCategory,
    supportPriority,
    setSupportSubject,
    setSupportMessage,
    setSupportOrderId,
    setSupportCategory,
    setSupportPriority,
    submitSupportRequest,
    retrySupportRequests,
    privacyDashboard,
    privacyLoading,
    privacyError,
    privacyRefreshing,
    exportRequesting,
    exportDownloading,
    consentsSaving,
    deletionSubmitting,
    privacyNotice,
    refreshPrivacyDashboard,
    requestPrivacyExport,
    downloadPrivacyExport,
    updatePrivacyConsents,
    requestAccountDeletion,
    handleLogout,
  }
}
